/**
 * feel-pdp-gallery.js
 * Thumbnail-driven image gallery for PDP.
 * Replaces Dawn media-gallery.js for feel-pdp-gallery snippet.
 * Idempotent — safe to call multiple times (Theme Editor re-renders).
 *
 * Features:
 *   - Thumbnail click  → activate slide + sync thumb
 *   - Prev/Next arrows → wrap-around navigation
 *   - Touch swipe      → horizontal ≥ 40 px triggers prev/next (vertical scroll safe)
 *   - Keyboard ← →    → on main image area (tabindex=0)
 *   - Arrows hidden    → when only 1 slide
 *   - Generic          → works for any number of slides (all 8 products)
 */
(function () {
  'use strict';

  function initGallery(gallery) {
    // Idempotency guard
    if (gallery.dataset.galInit) return;
    gallery.dataset.galInit = 'true';

    var main   = gallery.querySelector('[data-gallery-main]');
    var thumbs = Array.from(gallery.querySelectorAll('[data-thumb]'));
    var slides = Array.from(gallery.querySelectorAll('[data-slide]'));

    if (!main || thumbs.length === 0 || slides.length === 0) return;

    var count = slides.length;

    /* ── Core: activate slide N + sync thumb ─────────────────────── */
    function activate(index) {
      slides.forEach(function (slide) {
        var active = parseInt(slide.dataset.slide, 10) === index;
        if (active) {
          slide.dataset.active = 'true';
        } else {
          delete slide.dataset.active;
        }
      });
      thumbs.forEach(function (thumb) {
        var active = parseInt(thumb.dataset.thumb, 10) === index;
        if (active) {
          thumb.dataset.active = 'true';
          thumb.setAttribute('aria-selected', 'true');
        } else {
          delete thumb.dataset.active;
          thumb.setAttribute('aria-selected', 'false');
        }
      });
    }

    function currentIndex() {
      var active = thumbs.find(function (t) { return t.dataset.active === 'true'; });
      return active ? parseInt(active.dataset.thumb, 10) : 0;
    }

    function goPrev() { activate((currentIndex() - 1 + count) % count); }
    function goNext() { activate((currentIndex() + 1) % count); }

    /* ── Thumbnail click ──────────────────────────────────────────── */
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        activate(parseInt(thumb.dataset.thumb, 10));
      });
    });

    /* ── Keyboard a11y: ← → within thumb strip ───────────────────── */
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        var idx  = currentIndex();
        var next = e.key === 'ArrowRight'
          ? (idx + 1) % count
          : (idx - 1 + count) % count;
        activate(next);
        thumbs[next].focus();
      });
    });

    /* ── Prev / Next arrow buttons ────────────────────────────────── */
    var prevBtn = gallery.querySelector('[data-gallery-prev]');
    var nextBtn = gallery.querySelector('[data-gallery-next]');

    if (count <= 1) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
    } else {
      if (prevBtn) prevBtn.addEventListener('click', goPrev);
      if (nextBtn) nextBtn.addEventListener('click', goNext);
    }

    /* ── Keyboard ← → on main image area ─────────────────────────── */
    main.setAttribute('tabindex', '0');
    main.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); goPrev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    });

    /* ── Touch swipe ──────────────────────────────────────────────── */
    var touchStartX = 0;
    var touchStartY = 0;

    main.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].clientX;
      touchStartY = e.changedTouches[0].clientY;
    }, { passive: true });

    main.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      var dy = e.changedTouches[0].clientY - touchStartY;
      /* Ignore: too short, or mostly vertical (page scroll) */
      if (Math.abs(dx) < 40 || Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0) { goNext(); } else { goPrev(); }
    }, { passive: true });
  }

  function initAll() {
    document.querySelectorAll('[data-pdp-gallery]').forEach(initGallery);
  }

  /* Initial run */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme Editor re-renders sections — new DOM nodes, no guard reset needed */
  document.addEventListener('shopify:section:load', initAll);
})();
